import { generateId } from '../functions';
import { toBoolean } from '../functions/functions';
import { toArray } from '../object/object-guards.functions';

type JoinListStrategy =
  | ((values: Array<number | string>) => string)
  | 'and'
  | 'comma'
  | 'dash'
  | 'newline'
  | 'or'
  | 'slash'
  | 'space';

type JoinListValue<T> = ReadonlyArray<T> | ReadonlySet<T> | T;

/**
 * Capitalizes the first letter of a string.
 *
 * @param value - The string to capitalize
 * @returns The string with its first letter capitalized
 *
 * @example
 * capitalize('hello') // returns 'Hello'
 * capitalize('') // returns ''
 */
function capitalize(value: string): string {
  return !value || value.length === 0
    ? value
    : value.charAt(0).toUpperCase() + value.slice(1);
}

/**
 * Checks if the provided string contains at least one numeric digit.
 *
 * @param str - The string to check for numeric digits
 * @returns True if the string contains at least one number, false otherwise
 */
function containsNumber(str: string): boolean {
  const regex = /\d/u;

  return regex.test(str);
}

const formatCzSkPhone = (value: string | undefined): string => {
  if (!value) {
    return '';
  }

  const normalized = value.replaceAll(/\s+/gu, '');

  if (!/^(?:\+42[01])?\d{9}$/u.test(normalized)) {
    return value;
  }

  const hasPrefix = normalized.startsWith('+');
  const prefix = hasPrefix ? `${normalized.slice(0, 4)} ` : '';
  const digits = normalized.slice(hasPrefix ? 4 : 0);

  return `${prefix}${groupDigitsByThree(digits)}`;
};

/**
 * Formats a Czech (`+420`) or Slovak (`+421`) phone number for display.
 *
 * Accepts only:
 * - exactly 9 digits (e.g. `123456789` → `123 456 789`)
 * - 13 chars starting with `+420` (e.g. `+420123456789` → `+420 123 456 789`)
 * - any value starting with `+421` (e.g. `+421900123456` → `+421 900 123 456`)
 *
 * Throws `Error('It is not valid czech or slovak number')` for anything else.
 */
function groupDigitsByThree(digits: string): string {
  let formatted = '';
  for (let i = 0; i < digits.length; i += 1) {
    if (i > 0 && i % 3 === 0) {
      formatted += ' ';
    }
    formatted += digits.at(i) ?? '';
  }

  return formatted;
}

function insertAtIndex({
  char,
  index,
  str,
}: {
  char: string;
  index: number;
  str: string | undefined;
}): string {
  return str ? `${str.slice(0, index)}${char}${str.slice(index)}` : '';
}

function toAnchorName(value: string | null): string {
  if (!value) {
    return generateId();
  }

  const name = value
    .toLowerCase()
    .replaceAll(/[^a-z0-9_-]/gu, '')
    .replaceAll(/^[^a-z]+/gu, '');

  return `--${name.length > 0 ? name : generateId()}`;
}

// oxlint-disable-next-line max-statements
const joinList = (
  list: JoinListValue<number | string | null | undefined>,
  strategy: JoinListStrategy = 'and',
  locale = 'cs-CZ',
): string => {
  const arr = toArray(list);

  const filtered = arr
    .map((value: number | string | null | undefined) =>
      typeof value === 'string' ? value.trim() : value,
    )
    .filter(
      (v): v is number | string => v !== null && v !== undefined && v !== '',
    )
    .map(String);

  if (filtered.length === 0) {
    return '';
  }

  if (typeof strategy === 'function') {
    return strategy(filtered);
  }

  switch (strategy) {
    case 'comma': {
      return filtered.join(', ');
    }
    case 'dash': {
      return filtered.join(' - ');
    }
    case 'newline': {
      return filtered.join('\n');
    }
    case 'slash': {
      return filtered.join('/');
    }
    case 'space': {
      return filtered.join(' ');
    }
    /* oxlint-disable unicorn/no-useless-switch-case */
    case 'and':
    case 'or':
    default: {
      const type = strategy === 'and' ? 'conjunction' : 'disjunction';

      const formater = new Intl.ListFormat(locale, {
        style: 'long',
        type,
      });

      return formater.format(filtered);
    }
    /* oxlint-enable unicorn/no-useless-switch-case */
  }
};

/**
 * Converts string from `camelCase` to `kebab-case`
 */
function kebabCase(text: string): string {
  return text
    .replaceAll(/(?<first>[a-z])(?<second>[A-Z])/gu, '$<first>-$<second>')
    .replaceAll(/[\s_]+/gu, '-')
    .toLowerCase();
}

/**
 * Removes diacritics from the string
 */
function removeDiacritics(string: string): string {
  return string.normalize('NFD').replaceAll(/[\u{300}-\u{36F}]/gu, '');
}

/**
 * Converts a boolean value to a localized string representation ("Ano" for true, "Ne" for false).
 *
 * @param value - The boolean value to convert
 * @returns "Ano" if the value is true, "Ne" if the value is false
 */
function toBooleanString(value?: boolean | string): string {
  return toBoolean(value) ? 'Ano' : 'Ne';
}

export {
  capitalize,
  containsNumber,
  formatCzSkPhone,
  insertAtIndex,
  joinList,
  kebabCase,
  removeDiacritics,
  toAnchorName,
  toBooleanString,
};
export type { JoinListStrategy, JoinListValue };
